/**
 * Query hooks of the frame (1.I-1): the types list, a document's metadata, the release, the
 * story tree, pins, titles and Recent. The keys are the ones `live.ts` refreshes.
 */
import {
  type DocTitleV1,
  docsMeta,
  docsRecent,
  docsTitles,
  healthGet,
  linksTree,
  sidebarList,
  typesList,
} from '@codex/contracts';
import { type QueryClient, queryOptions, useQuery } from '@tanstack/react-query';
import { api } from '../auth/client.js';
import type { ChainMeta } from './breadcrumbs.js';
import { MAX_TITLE_IDS } from './pins-model.js';

export const typesKey = ['types'] as const;
export const healthKey = ['health'] as const;
export const sidebarKey = ['sidebar'] as const;
export const metaKey = (id: string) => ['meta', id] as const;
export const treeKey = (parentId: string | null) => ['tree', parentId] as const;
export const titlesKey = (ids: readonly string[]) => ['titles', ids.join(',')] as const;
export const recentKey = (limit: number) => ['recent', limit] as const;

export function useTypes() {
  return useQuery({
    queryKey: typesKey,
    queryFn: async () => (await api(typesList)).items,
    // The list has its own "Try again".
    retry: false,
  });
}

export function useHealth() {
  return useQuery({ queryKey: healthKey, queryFn: () => api(healthGet), retry: false });
}

/** One `docs.meta` read through the cache, so the chain walk and the header share answers. */
export function fetchMeta(client: QueryClient, id: string): Promise<ChainMeta> {
  return client.fetchQuery({
    queryKey: metaKey(id),
    queryFn: () => api(docsMeta, { params: { id } }),
  });
}

/** One level of the story tree: the top level for `null`, else the pages homed under a parent. */
export function treeQueryOptions(parentId: string | null) {
  return queryOptions({
    queryKey: treeKey(parentId),
    queryFn: async () =>
      (await api(linksTree, { query: parentId === null ? {} : { parentId } })).items,
    retry: false,
  });
}

export function useSidebarItems() {
  return useQuery({
    queryKey: sidebarKey,
    queryFn: async () => (await api(sidebarList)).items,
    retry: false,
  });
}

/** Titles for a set of ids, one request per distinct set (at most 500). */
export function useTitles(ids: readonly string[]) {
  const asked = ids.slice(0, MAX_TITLE_IDS);
  return useQuery({
    queryKey: titlesKey(asked),
    enabled: asked.length > 0,
    queryFn: async (): Promise<DocTitleV1[]> =>
      (await api(docsTitles, { body: { ids: [...asked] } })).titles,
    retry: false,
  });
}

export function useRecent(limit: number) {
  return useQuery({
    queryKey: recentKey(limit),
    queryFn: async () => (await api(docsRecent, { query: { limit } })).items,
    retry: false,
  });
}
